/**
 * UserAvatar Component
 * Unified avatar component supporting identity and profile-card variants.
 */

import React from 'react';
import { Avatar, Tooltip, Typography, Box, Divider } from '@mui/material';
import { Person as PersonIcon } from '@mui/icons-material';
import { UserIdentity } from '@/types/user';
import { AVATAR_SIZES } from '../../constants';

type IdentityAvatarUser = {
  owner_full_name?: string | null;
  owner_email?: string | null;
  owner_username?: string | null;
  owner_id?: string | null;
  user_id?: string | null;
  created_by?: string | null;
  full_name?: string | null;
  email?: string | null;
  username?: string | null;
};

type ProfileAvatarUser = {
  username?: string;
  full_name?: string;
  role?: string;
} | null;

export interface UserAvatarProps {
  /** Rendering mode; defaults to compact identity avatar. */
  mode?: 'identity' | 'profile';
  /** User data object with identity fields */
  user: IdentityAvatarUser | ProfileAvatarUser;
  /** Avatar size */
  size?: 'small' | 'medium' | 'large';
  /** Custom size in pixels (overrides size prop) */
  customSize?: number;
  /** Show tooltip with user info */
  showTooltip?: boolean;
  /** Custom tooltip text */
  tooltipText?: string;
  /** Additional sx props for identity mode avatar */
  sx?: Record<string, unknown>;
  /** Optional permission/role label shown in profile mode */
  permissionLabel?: string;
}

const SIZE_MAP = {
  small: AVATAR_SIZES.SMALL,
  medium: AVATAR_SIZES.MEDIUM,
  large: AVATAR_SIZES.LARGE,
} as const;

/**
 * Displays user avatar with initials and consistent colors.
 * In profile mode, renders the full drawer header user card.
 */
export const UserAvatar: React.FC<UserAvatarProps> = ({
  mode = 'identity',
  user,
  size = 'medium',
  customSize,
  showTooltip = true,
  tooltipText,
  sx = {},
  permissionLabel,
}) => {
  if (mode === 'profile') {
    const profileUser = user as ProfileAvatarUser;
    return (
      <>
        <Box sx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          backgroundColor: 'primary.main',
          color: 'white',
        }}>
          <Avatar
            sx={{
              width: 60,
              height: 60,
              mb: 1,
              backgroundColor: 'white',
              color: 'primary.main',
              fontFamily: '"Roboto", "Segoe UI", "Helvetica Neue", Arial, sans-serif',
              fontWeight: 500,
            }}
          >
            {profileUser?.username?.charAt(0).toUpperCase() || 'U'}
          </Avatar>
          <Typography variant="h6">{profileUser?.full_name || profileUser?.username || 'User'}</Typography>
          {permissionLabel && <Typography variant="body2">{permissionLabel}</Typography>}
        </Box>
        <Divider />
      </>
    );
  }

  const identity = UserIdentity.fromDocument(user as IdentityAvatarUser);
  const avatarSize = customSize || SIZE_MAP[size];

  // If no user identity data, show default icon
  if (identity.isEmpty()) {
    const avatar = (
      <Avatar
        sx={{
          bgcolor: 'action.disabled',
          width: avatarSize,
          height: avatarSize,
          fontSize: avatarSize * 0.5,
          ...sx,
        }}
      >
        <PersonIcon fontSize="small" />
      </Avatar>
    );

    return showTooltip ? (
      <Tooltip title={tooltipText || 'Unknown User'}>
        {avatar}
      </Tooltip>
    ) : avatar;
  }

  const avatar = (
    <Avatar
      sx={{
        bgcolor: identity.getAvatarColor(),
        width: avatarSize,
        height: avatarSize,
        fontSize: avatarSize * 0.4,
        fontWeight: 500,
        ...sx,
      }}
    >
      {identity.getInitials()}
    </Avatar>
  );

  return showTooltip ? (
    <Tooltip title={tooltipText || identity.getTooltipText()}>
      {avatar}
    </Tooltip>
  ) : avatar;
};

export default UserAvatar;
